import React, { useEffect, useState, useRef } from 'react'
import {Shield, Plus, Trash2, CheckCircle, XCircle, Upload, Users, FolderOpen, X, Edit} from 'lucide-react'
import { useProjects, Project } from '../hooks/useProjects'
import { useFileUpload } from '../hooks/useFileUpload'
import { lumi } from '../lib/lumi'
import { ProfileEditModal } from './ProfileEditModal'

interface AdminPanelProps {
  adminUsername: string
  onSignOut: () => void
}

export function AdminPanel({ adminUsername, onSignOut }: AdminPanelProps) {
  const { projects, loading, fetchProjects, createProject, updateProject, deleteProject } = useProjects()
  const { uploadSingleFile, uploading } = useFileUpload()
  
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [selectedProject, setSelectedProject] = useState<Project | null>(null)
  const [filter, setFilter] = useState<'all' | 'active' | 'inactive'>('all')
  
  // Create project form state
  const [projectName, setProjectName] = useState('')
  const [websiteUrl, setWebsiteUrl] = useState('')
  const [description, setDescription] = useState('')
  const [ownerEmail, setOwnerEmail] = useState('')
  const [isDemo, setIsDemo] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string>('')
  const [creating, setCreating] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    loadProjects()
  }, [filter])

  const loadProjects = async () => {
    if (filter === 'all') {
      await fetchProjects({})
    } else if (filter === 'active') {
      await fetchProjects({ isActive: true, subscriptionStatus: 'active' })
    } else {
      await fetchProjects({ isActive: false })
    }
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file')
        return
      }
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB')
        return
      }
      setSelectedFile(file)
      setPreviewUrl(URL.createObjectURL(file))
    }
  }

  const handleCreateProject = async () => {
    if (!projectName || !websiteUrl || !ownerEmail) {
      alert('Please fill in all required fields')
      return
    }

    try {
      setCreating(true)

      let bannerUrl = ''
      if (selectedFile) {
        const uploadedUrls = await uploadSingleFile(selectedFile)
        if (uploadedUrls && uploadedUrls.length > 0) {
          bannerUrl = uploadedUrls[0]
        }
      }

      // Create project directly without Stripe (admin bypass)
      const newProject = await createProject({
        projectName,
        websiteUrl,
        description,
        bannerImageUrl: bannerUrl || '',
        ownerEmail,
        ownerId: 'admin-created',
        subscriptionStatus: 'active', // Bypass payment - MUST be 'active' to show on website
        isActive: true, // Activate immediately
        isDemo: isDemo,
        stripeCustomerId: 'admin-bypass',
        stripeSubscriptionId: 'admin-bypass'
      })

      alert('Project created successfully!')
      setShowCreateModal(false)
      resetForm()
      await loadProjects()
    } catch (error) {
      console.error('Create error:', error)
      alert('Failed to create project. Please try again.')
    } finally {
      setCreating(false)
    }
  }

  const resetForm = () => {
    setProjectName('')
    setWebsiteUrl('')
    setDescription('')
    setOwnerEmail('')
    setIsDemo(false)
    setSelectedFile(null)
    setPreviewUrl('')
  }



  const handleEditProject = (project: Project) => {
    setSelectedProject(project)
    setShowEditModal(true)
  }

  const handleDeleteProject = async (projectId: string, projectName: string) => {
    if (confirm(`Are you sure you want to permanently delete "${projectName}"? This action cannot be undone.`)) {
      try {
        await deleteProject(projectId)
        alert('Project deleted successfully')
        await loadProjects()
      } catch (error) {
        console.error('Delete error:', error)
        alert('Failed to delete project')
      }
    }
  }

  const handleToggleDemo = async (projectId: string, currentStatus: boolean) => {
    try {
      await updateProject(projectId, {
        isDemo: !currentStatus
      })
      await loadProjects()
    } catch (error) {
      console.error('Toggle demo error:', error)
      alert('Failed to toggle demo status')
    }
  }

  if (loading && projects.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0A0E27] via-[#0d1339] to-[#0A0E27] flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0A0E27] via-[#0d1339] to-[#0A0E27]">
      {/* Header */}
      <header className="border-b border-white/10 bg-[#0A0E27]/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-r from-[#0052FF] to-[#60A5FA] rounded-lg">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">Admin Panel</h1>
                <p className="text-xs text-gray-400">Base Builders Management</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-gray-300 text-sm">Admin: {adminUsername}</span>
              <button
                onClick={onSignOut}
                className="px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors text-sm"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Info Banner */}
        <div className="mb-6 bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
          <p className="text-sm text-gray-300">
            <strong className="text-white">ℹ️ Auto-Listing:</strong> Projects that complete Stripe payment are automatically activated and listed on the website. No manual approval needed.
          </p>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white/5 border border-white/10 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-2">
              <FolderOpen className="w-5 h-5 text-[#60A5FA]" />
              <span className="text-gray-400 text-sm">Total Projects</span>
            </div>
            <p className="text-3xl font-bold text-white">{projects.length}</p>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-2">
              <CheckCircle className="w-5 h-5 text-green-400" />
              <span className="text-gray-400 text-sm">Active Projects</span>
            </div>
            <p className="text-3xl font-bold text-white">
              {projects.filter(p => p.isActive && p.subscriptionStatus === 'active').length}
            </p>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-2">
              <Users className="w-5 h-5 text-[#60A5FA]" />
              <span className="text-gray-400 text-sm">Unique Owners</span>
            </div>
            <p className="text-3xl font-bold text-white">
              {new Set(projects.map(p => p.ownerEmail)).size}
            </p>
          </div>
        </div>

        {/* Actions Bar */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div className="flex gap-2">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filter === 'all'
                  ? 'bg-gradient-to-r from-[#0052FF] to-[#60A5FA] text-white'
                  : 'bg-white/5 text-gray-400 hover:bg-white/10'
              }`}
            >
              All ({projects.length})
            </button>
            <button
              onClick={() => setFilter('active')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filter === 'active'
                  ? 'bg-gradient-to-r from-[#0052FF] to-[#60A5FA] text-white'
                  : 'bg-white/5 text-gray-400 hover:bg-white/10'
              }`}
            >
              Active
            </button>
            <button
              onClick={() => setFilter('inactive')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filter === 'inactive'
                  ? 'bg-gradient-to-r from-[#0052FF] to-[#60A5FA] text-white'
                  : 'bg-white/5 text-gray-400 hover:bg-white/10'
              }`}
            >
              Inactive
            </button>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#0052FF] to-[#60A5FA] text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
          >
            <Plus className="w-5 h-5" />
            Create Free Project
          </button>
        </div>

        {/* Projects Table */}
        <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-white/5 border-b border-white/10">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Project</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Owner</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Status</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Subscription</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Created</th>
                  <th className="px-6 py-4 text-right text-sm font-medium text-gray-400">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {projects.map((project) => (
                  <tr key={project._id} className="hover:bg-white/5 transition-colors">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        {project.bannerImageUrl && project.bannerImageUrl.startsWith('http') ? (
                          <img
                            src={project.bannerImageUrl}
                            alt={project.projectName}
                            className="w-16 h-10 object-cover rounded border border-white/10"
                            onError={(e) => {
                              console.error('Failed to load banner image:', project.bannerImageUrl)
                              e.currentTarget.style.display = 'none'
                              const placeholder = e.currentTarget.nextElementSibling
                              if (placeholder) placeholder.classList.remove('hidden')
                            }}
                          />
                        ) : null}
                        <div className={`w-16 h-10 bg-red-500/10 rounded border border-red-500/30 flex items-center justify-center ${project.bannerImageUrl && project.bannerImageUrl.startsWith('http') ? 'hidden' : ''}`}>
                          <span className="text-xs text-red-400">Invalid</span>
                        </div>
                        <div>
                          <p className="text-white font-medium">{project.projectName}</p>
                          <p className="text-sm text-gray-400 truncate max-w-xs">{project.websiteUrl}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-300 text-sm">{project.ownerEmail}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${
                        project.isActive
                          ? 'bg-green-500/20 text-green-400'
                          : 'bg-gray-500/20 text-gray-400'
                      }`}>
                        {project.isActive ? (
                          <>
                            <CheckCircle className="w-3 h-3" />
                            Active
                          </>
                        ) : (
                          <>
                            <XCircle className="w-3 h-3" />
                            Inactive
                          </>
                        )}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${
                        project.subscriptionStatus === 'active'
                          ? 'bg-blue-500/20 text-blue-400'
                          : 'bg-yellow-500/20 text-yellow-400'
                      }`}>
                        {project.subscriptionStatus}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-400 text-sm">
                      {new Date(project.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex justify-end gap-2">
                        <label
                          className="flex items-center gap-2 px-3 py-2 bg-purple-500/20 text-purple-400 rounded-lg hover:bg-purple-500/30 transition-colors cursor-pointer"
                          title="Toggle Demo Status"
                        >
                          <input
                            type="checkbox"
                            checked={project.isDemo || false}
                            onChange={() => handleToggleDemo(project._id, project.isDemo || false)}
                            className="w-4 h-4 rounded border-purple-400 text-purple-500 focus:ring-purple-500 focus:ring-offset-0 bg-transparent cursor-pointer"
                          />
                          <span className="text-xs font-medium">DEMO</span>
                        </label>
                        <button
                          onClick={() => handleEditProject(project)}
                          className="p-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors"
                          title="Edit Project"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteProject(project._id, project.projectName)}
                          className="p-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
                          title="Delete Project"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {projects.length === 0 && (
            <div className="text-center py-12">
              <FolderOpen className="w-12 h-12 text-gray-500 mx-auto mb-3" />
              <p className="text-gray-400">No projects found</p>
            </div>
          )}
        </div>
      </div>

      {/* Create Project Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-[#0d1339] border border-white/10 rounded-2xl max-w-2xl w-full p-8 my-8">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-white">Create Free Project (Admin)</h3>
              <button
                onClick={() => {
                  setShowCreateModal(false)
                  resetForm()
                }}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Project Name */}
              <div>
                <label className="block text-gray-300 mb-2">Project Name *</label>
                <input
                  type="text"
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  placeholder="e.g., CryptoVerse NFT"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#0052FF]"
                />
              </div>

              {/* Website URL */}
              <div>
                <label className="block text-gray-300 mb-2">Website URL *</label>
                <input
                  type="url"
                  value={websiteUrl}
                  onChange={(e) => setWebsiteUrl(e.target.value)}
                  placeholder="https://yourproject.com"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#0052FF]"
                />
              </div>

              {/* Owner Email */}
              <div>
                <label className="block text-gray-300 mb-2">Owner Email *</label>
                <input
                  type="email"
                  value={ownerEmail}
                  onChange={(e) => setOwnerEmail(e.target.value)}
                  placeholder="owner@example.com"
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#0052FF]"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-gray-300 mb-2">Description (Optional)</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Brief description of the project..."
                  rows={3}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#0052FF]"
                />
              </div>

              {/* Demo Checkbox */}
              <div>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isDemo}
                    onChange={(e) => setIsDemo(e.target.checked)}
                    className="w-5 h-5 rounded border-purple-400 text-purple-500 focus:ring-purple-500 focus:ring-offset-0 bg-transparent cursor-pointer"
                  />
                  <span className="text-gray-300">Mark as Demo Project</span>
                </label>
              </div>

              {/* Banner Upload */}
              <div>
                <label className="block text-gray-300 mb-2">Banner Image (1200x400px recommended)</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                
                {previewUrl ? (
                  <div className="relative">
                    <img
                      src={previewUrl}
                      alt="Preview"
                      className="w-full h-48 object-cover rounded-lg border border-white/10"
                    />
                    <button
                      onClick={() => {
                        setSelectedFile(null)
                        setPreviewUrl('')
                      }}
                      className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full h-48 border-2 border-dashed border-white/20 rounded-lg flex flex-col items-center justify-center gap-3 hover:border-[#0052FF] transition-colors"
                  >
                    <Upload className="w-10 h-10 text-gray-500" />
                    <span className="text-gray-400">Click to upload banner image</span>
                    <span className="text-sm text-gray-500">Max size: 5MB</span>
                  </button>
                )}
              </div>

              {/* Info Box */}
              <div className="bg-[#0052FF]/10 border border-[#0052FF]/30 rounded-lg p-4">
                <p className="text-sm text-gray-300">
                  <strong className="text-white">Admin Override:</strong> This project will be created without payment and will be immediately activated with full access.
                </p>
              </div>

              {/* Submit Button */}
              <button
                onClick={handleCreateProject}
                disabled={creating || uploading || !projectName || !websiteUrl || !ownerEmail}
                className="w-full px-6 py-3 bg-gradient-to-r from-[#0052FF] to-[#60A5FA] text-white rounded-lg font-bold hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {creating || uploading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Creating...
                  </>
                ) : (
                  <>
                    <Plus className="w-5 h-5" />
                    Create Project (No Payment Required)
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Profile Modal */}
      {showEditModal && selectedProject && (
        <ProfileEditModal
          project={selectedProject}
          onClose={() => {
            setShowEditModal(false)
            setSelectedProject(null)
            loadProjects()
          }}
        />
      )}
      
      {/* Note: Footer is now rendered in App.tsx for all pages */}
    </div>
  )
}
